import type {
  StoreOverview,
  StoreOverviewKpi,
} from "@/features/store-overview/storeOverviewSchemas";

export const MAX_CUSTOM_RANGE_DAYS = 90;

export function isValidCustomRange(start: string, end: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(start) || !/^\d{4}-\d{2}-\d{2}$/u.test(end)) {
    return false;
  }
  const startTime = Date.parse(`${start}T00:00:00Z`);
  const endTime = Date.parse(`${end}T00:00:00Z`);
  if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) return false;
  const spanDays = Math.round((endTime - startTime) / 86_400_000) + 1;
  return spanDays >= 1 && spanDays <= MAX_CUSTOM_RANGE_DAYS;
}

function currencyPrefix(currency: string) {
  const parts = new Intl.NumberFormat("zh-CN", {
    currency,
    currencyDisplay: "narrowSymbol",
    style: "currency",
  }).formatToParts(0);
  return parts.find((part) => part.type === "currency")?.value ?? currency;
}

function formatMinor(value: string, currency: string) {
  const minor = BigInt(value);
  const negative = minor < 0n;
  const absolute = negative ? -minor : minor;
  const whole = absolute / 100n;
  const fraction = String(absolute % 100n).padStart(2, "0");
  const grouped = new Intl.NumberFormat("zh-CN").format(whole);
  return `${negative ? "-" : ""}${currencyPrefix(currency)}${grouped}.${fraction}`;
}

function formatBps(value: string) {
  const number = Number(value) / 10_000;
  return Number.isFinite(number) ? `${number.toFixed(2)}×` : "—";
}

export function formatStoreOverviewKpi(kpi: StoreOverviewKpi) {
  if (kpi.value === null) return "—";
  if (kpi.unit === "minor" && kpi.currency) {
    return formatMinor(kpi.value, kpi.currency);
  }
  if (kpi.unit === "basis_points") return formatBps(kpi.value);
  return new Intl.NumberFormat("zh-CN").format(BigInt(kpi.value));
}

export function formatStoreOverviewAmount(value: string, currency: string) {
  return formatMinor(value, currency);
}

export function formatComparisonDelta(value: string | null) {
  if (value === null) return null;
  const percentage = Number(value) / 100;
  if (!Number.isFinite(percentage)) return null;
  const prefix = percentage > 0 ? "+" : "";
  return `${prefix}${percentage.toFixed(1)}%`;
}

export function formatShareBps(value: string | null) {
  if (value === null) return "—";
  const percentage = Number(value) / 100;
  return Number.isFinite(percentage) ? `${percentage.toFixed(1)}%` : "—";
}

export function formatStoreOverviewAsOf(overview: StoreOverview) {
  if (!overview.asOf) return "暂无共同数据水位";
  return new Intl.DateTimeFormat("zh-CN", {
    day: "2-digit",
    hour: "2-digit",
    hour12: false,
    minute: "2-digit",
    month: "2-digit",
    timeZone: overview.queryScope.reportingTimezone,
  }).format(new Date(overview.asOf));
}

export function storeOverviewMissingReason(code: string) {
  const messages: Readonly<Record<string, string>> = {
    attribution_incompatible: "广告归因窗口不一致",
    comparison_attribution_incompatible: "上一周期广告归因窗口不一致",
    comparison_window_uncovered: "上一周期数据窗口覆盖不足",
    credential_expired: "授权已过期",
    fx_rate_invalid: "汇率事实无效",
    fx_rate_missing: "缺少可验证汇率",
    invalid_money_value: "金额事实无效",
    metric_missing: "指标尚未采集",
    not_bound: "尚未绑定该数据来源",
    not_collected: "尚未采集到该指标",
    profit_components_missing: "利润计算组件不完整",
    provider_contract_unavailable: "平台采集合同暂不可用",
    provider_unsupported: "平台暂不支持该指标",
    source_authorization_unavailable: "来源授权不可用",
    source_snapshot_invalid: "来源快照未通过校验",
    source_snapshot_missing: "来源快照缺失",
    source_snapshot_partial: "来源快照覆盖不完整",
    source_snapshot_stale: "来源快照已过期",
    source_stale: "来源数据已过期",
    source_window_uncovered: "当前数据窗口覆盖不足",
  };
  return messages[code] ?? code.replaceAll("_", " ");
}
